import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { postApiJson } from "../services/api";
import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogClose,
} from "~/components/ui/dialog";
import { Alert, AlertDescription } from "~/components/ui/alert";

interface ReportModalProps {
  word: string;
  inputText: string;
  outputText: string;
  isOpen: boolean;
  onClose: () => void;
}

export function ReportModal({
  word,
  inputText,
  outputText,
  isOpen,
  onClose,
}: ReportModalProps) {
  const { t } = useTranslation();
  const [body, setBody] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const handleSubmit = useCallback(async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      await postApiJson<{ message: string }>("/reports", {
        text: word,
        body,
        content: inputText,
        result: outputText,
        url: window.location.href,
      });
      setShowSuccess(true);
      setBody("");
      setTimeout(() => {
        setShowSuccess(false);
        onClose();
      }, 1500);
    } catch {
      // silent fail
    } finally {
      setIsSubmitting(false);
    }
  }, [body, inputText, isSubmitting, onClose, outputText, word]);

  const handleClose = useCallback(() => {
    setBody("");
    setShowSuccess(false);
    onClose();
  }, [onClose]);

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("report.title")}</DialogTitle>
        </DialogHeader>

        {showSuccess ? (
          <div className="flex p-4 justify-center">
            <p className="font-bold text-lg text-green-500">
              {t("report.success")}
            </p>
          </div>
        ) : (
          <>
            <Alert>
              <AlertDescription>{word}</AlertDescription>
            </Alert>
            <textarea
              className="block w-full rounded-lg border border-input px-3 py-2 text-sm"
              rows={5}
              placeholder={t("report.placeholder")}
              value={body}
              onChange={(e) => setBody(e.target.value)}
            />
            <DialogFooter>
              <DialogClose asChild>
                <Button variant="outline">{t("report.close")}</Button>
              </DialogClose>
              <Button onClick={handleSubmit} disabled={isSubmitting}>
                {t("report.send")}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
